import styles from './TestResults.module.css'
import { AI_DECISION } from '../../../../global/constants/audit'

interface AccuracySummaryProps {
  testResults: any[]
  testSummary: any
  tokenSummary: any
  testDateRange: { earliest: string; latest: string } | null
  correctedAudits: Set<string>
}

function AccuracySummary({
  testResults,
  testSummary,
  tokenSummary,
  testDateRange,
  correctedAudits,
}: AccuracySummaryProps) {
  const successfulTests = testResults.filter((r) => !r.error)

  // Account for manual corrections when calculating metrics
  const isCorrect = (result: any) => {
    // If originally incorrect but manually corrected, count as correct
    if (!result.correct && correctedAudits.has(result.auditObjectId)) {
      return true
    }
    // If originally correct but manually un-corrected, count as incorrect
    if (result.correct && correctedAudits.has(result.auditObjectId)) {
      return false
    }
    // Otherwise use original correctness
    return result.correct
  }

  const correctTests = successfulTests.filter((r) => isCorrect(r))
  const passTests = successfulTests.filter(
    (r) => r.humanDecision === AI_DECISION.pass,
  )
  const failTests = successfulTests.filter(
    (r) => r.humanDecision === AI_DECISION.fail,
  )
  const correctPasses = passTests.filter((r) => isCorrect(r))
  const correctFails = failTests.filter((r) => isCorrect(r))

  const overallAccuracy = Math.round(
    (correctTests.length / successfulTests.length) * 100,
  )

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 90) return 'var(--accuracy-good)' // green
    if (accuracy >= 70) return 'var(--accuracy-warning)' // yellow
    return 'var(--accuracy-critical)' // red
  }

  return (
    <>
      <div className={styles.summarySection}>
        {/* Big Accuracy Display */}
        <div className={styles.bigAccuracyDisplay}>
          <div className={styles.accuracyLabel}>AI Accuracy</div>
          <div
            className={styles.accuracyPercentage}
            style={{ color: getAccuracyColor(overallAccuracy) }}
          >
            {overallAccuracy}%
          </div>
        </div>

        {/* Metadata Chips */}
        <div className={styles.metadataChips}>
          <div className={styles.metadataChip}>
            <span className={styles.chipLabel}>Pass Agreement:</span>
            <span className={styles.chipValue}>
              {correctPasses.length}/{passTests.length}
            </span>
          </div>
          <div className={styles.metadataChip}>
            <span className={styles.chipLabel}>Fail Agreement:</span>
            <span className={styles.chipValue}>
              {correctFails.length}/{failTests.length}
            </span>
          </div>
          {tokenSummary && (
            <div className={styles.metadataChip}>
              <span className={styles.chipLabel}>Tokens:</span>
              <span className={styles.chipValue}>
                ${tokenSummary.estimatedCost.toFixed(4)}
              </span>
            </div>
          )}
          {testDateRange && (
            <div className={styles.metadataChip}>
              <span className={styles.chipLabel}>Date Range:</span>
              <span className={styles.chipValue}>
                {testDateRange.earliest === testDateRange.latest
                  ? testDateRange.earliest
                  : `${testDateRange.earliest} - ${testDateRange.latest}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {testSummary && testSummary.failed > 0 && (
        <div className={styles.failedAuditsNotice}>
          ⚠️ {testSummary.failed} audit
          {testSummary.failed !== 1 ? 's' : ''} failed due to technical errors
          and {testSummary.failed !== 1 ? 'were' : 'was'} excluded from results
        </div>
      )}
    </>
  )
}

export default AccuracySummary
